import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GeneralV3Response, LoadingType } from "../../../types/CommonTypes";
import { useAppSelector } from "../redux-hooks";
import {
  getIVRNoteById,
  removeIVRDocumentById,
  uploadIVRDocumentData,
  uploadIVRNotesData,
  getIVRDocuments,
  getIVRDocumentByDocumentId,
  updatePatientDocumentInfo,
} from "../../../service/IVRService";

export type IVRDocumentInfo = {
  ivrId: any;
  documentId?: number;
  documentName: string;
  content: string;
  documentType?: string;
  type?: string;
  fileType: string;
  auditTimestamp? : string;
};

export type IVRNotes = {
  attachmentId: number;
  noteType: string;
  content?: string;
  attachment: string;
  documentName: string;
  fileType: string;
  xrefTable: string;
  xrefId: string;
  auditUserName?: string,
  auditTimestamp? : string,
  comments?: string, 
};

export type IVRNoteDetail = {
  noteId: number;
  xrefTable: string;
  xrefId: number;
  noteType: string;
  content?: string;
  attachementId: number;
  documentName: string;
  fileType: string;  
  auditUserName?: string,
  auditTimestamp? : string,
  comments?: string, 
};

export type UpdatePatientDocumentInfo = {
  ivrId: number;
  documentType: string;
  patientId: number;
  documentIds: number[];
}

type IVRDocumentState = {
  ivrDocumentInfo?: IVRDocumentInfo | null;
  ivrDocumentInfoList?: IVRDocumentInfo[];
  IVRNotes?:IVRNotes | null;
  IVRNotesList?:IVRNoteDetail[];
  status?: LoadingType;
  documentUploadInfo?: any;
  documentRemoveInfo?: any;
  updatePatientInfoDocuments?: UpdatePatientDocumentInfo | null;
};

const initialState: IVRDocumentState = {
  ivrDocumentInfo: undefined,
  status: "idle",
};

export const uploadIVRDocument = createAsyncThunk(
  "uploadIVRDocument",
  async (data: any) => {
    const response = await uploadIVRDocumentData(data);
    // return response.data;
    return response?.data; // CS-1984
  }
);

export const uploadIVRNotes = createAsyncThunk(
  "uploadIVRNotes",
  async (data: any) => {
    const response = await uploadIVRNotesData(data);
    // return response.data;
    return response?.data; // CS-1984
  }
);

export const removeIVRDocument = createAsyncThunk(
  "removeIVRDocument",
  async (documentId: string) => {
    const response = await removeIVRDocumentById(documentId);
    // return response.data;
    return response?.data; // CS-1984
  }
);

export const fetchIVRNotesById = createAsyncThunk("fetchIVRNotesById", async (xrefTable: number) => {
  const response = await getIVRNoteById(xrefTable, 'IVR');
  // const v3Response = response.data;
  const v3Response = response?.data; // CS-1984
  return v3Response;
});

export const fetchIVRDocuments = createAsyncThunk("fetchIVRDocuments", async (data: any) => {
  const response = await getIVRDocuments(data.ivrId, data.patientId);
  // const v3Response = response.data;
  const v3Response = response?.data; // CS-1984
  return v3Response;
});

export const fetchDocumentById = createAsyncThunk('fetchDocumentById',async( documentId: number ) => {
  const response = await getIVRDocumentByDocumentId(documentId);
  const v3Response = response.data;
  return v3Response.data;
});

export const updatePatientInfoDocuments = createAsyncThunk('updatePatientInfoDocuments',async( documentInfo: UpdatePatientDocumentInfo ) => {
  const response = await updatePatientDocumentInfo(documentInfo);
  // const v3Response = response.data;
  const v3Response = response?.data; // CS-1984
  // return v3Response.data;
  return v3Response?.data; // CS-1984
});

const ivrDocumentSlice = createSlice({
  name: "ivrDocuments",
  initialState,
  reducers: {
    resetIvrDocumentInfoList: (state: any) => {
      state.ivrDocumentInfoList = null
    },
    resetIvrNotesList: (state: any) => {
      state.IVRNotesList =  []
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadIVRDocument.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        uploadIVRDocument.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
          // state.documentUploadInfo = action.payload.data;
          state.documentUploadInfo = action.payload?.data; // CS-1984
        }
      )
      .addCase(uploadIVRDocument.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(uploadIVRNotes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        uploadIVRNotes.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
        }
      )
      .addCase(uploadIVRNotes.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(removeIVRDocument.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        removeIVRDocument.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
          // state.documentRemoveInfo = action.payload.data;
          state.documentRemoveInfo = action.payload?.data; // CS-1984
        }
      )
      .addCase(removeIVRDocument.rejected, (state, action) => {
        state.status = "error";
      }).addCase(fetchIVRNotesById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchIVRNotesById.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
          // state.IVRNotesList = action.payload.data;
          state.IVRNotesList = action.payload?.data; // CS-1984
        }
      )
      .addCase(fetchIVRNotesById.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(fetchIVRDocuments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchIVRDocuments.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
          // state.ivrDocumentInfoList = action.payload.data;
          state.ivrDocumentInfoList = action.payload?.data; // CS-1984
        }
      )
      .addCase(fetchIVRDocuments.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(fetchDocumentById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDocumentById.fulfilled,(state, action: PayloadAction<IVRDocumentInfo>) => {
          state.status = "success";
          state.ivrDocumentInfo = action.payload;
        }
      )
      .addCase(fetchDocumentById.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(updatePatientInfoDocuments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        updatePatientInfoDocuments.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.status = "success";
          // state.updatePatientInfoDocuments = action.payload.data;
          state.updatePatientInfoDocuments = action.payload?.data; // CS-1984
        }
      )
      .addCase(updatePatientInfoDocuments.rejected, (state, action) => {
        state.status = "error";
      })
      ;
  },
});

export const { resetIvrDocumentInfoList, resetIvrNotesList } = ivrDocumentSlice.actions;
export const useIVRDocumentSlice = () =>
  useAppSelector((state) => state.ivrDocumentSlice);
export default ivrDocumentSlice.reducer;
